import React, { useState } from 'react';

import { CSVLink } from 'react-csv';

import useQuery from '../../../helpers/JIFI/useQuery';

import SupplyChainNav from '../../../components/supply-chain/nav.js';
import { Col } from '@shadcn/components/ui/col.jsx';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card.jsx';
import { Button } from '@shadcn/components/ui/button.jsx';
import { Input } from '@shadcn/components/ui/input.jsx';
import { TableSkeleton } from '@shadcn/components/ui/table.jsx';

const REGIONS = ['', 'AU', 'US', 'UK', 'EU'];

// Planning view: on-hand vs origin->WH in-transit per SKU per market.
const TotalStockList = () => {
  const dateString = new Date().toDateString().replace(/ /g, '-');
  const [region, setRegion] = useState('');
  const [skuFilter, setSkuFilter] = useState('');

  const [rows, rowsState] = useQuery({
    url: `/total-stock`,
    queryData: { region },
  });

  const [drill, setDrill] = useState(null); // { sku, region }
  const [drillRows] = useQuery({
    url: `/total-stock/in-transit-detail`,
    queryData: { sku: drill?.sku, region: drill?.region },
    runQuery: !!drill,
  });

  const data = Array.isArray(rows) ? rows : [];
  const filtered = skuFilter
    ? data.filter((r) => r.sku?.toLowerCase().includes(skuFilter.toLowerCase()))
    : data;

  const csvData = [
    ['Market', 'SKU', 'On Hand', 'In Transit (origin→WH)', 'Total'],
    ...filtered.map((r) => [
      r.region,
      r.sku,
      r.onHand,
      r.inTransitOriginToWh,
      (r.onHand ?? 0) + (r.inTransitOriginToWh ?? 0),
    ]),
  ];

  const handleSkuKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) setSkuFilter(e.target.value);
  };

  return (
    <SupplyChainNav>
      <div>
        <Col className="w-full">
          <Card className="pt-6 mb-10">
            <CardBody>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h4 className="mb-3">Total Stock by Market</h4>
                <CSVLink
                  style={{ float: 'right', marginBottom: '1.25rem' }}
                  data={csvData}
                  filename={`TOTAL-STOCK-${dateString}.csv`}
                >
                  <Button type="button" variant="secondary" className="btn-md btn-rounded">
                    CSV
                  </Button>
                </CSVLink>
              </div>

              <div className="flex gap-4 mb-3">
                <div>
                  <label className="control-label">MARKET</label>
                  <select
                    className="form-control"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  >
                    {REGIONS.map((r) => (
                      <option key={r || 'all'} value={r}>
                        {r || 'All markets'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="control-label">FILTER SKU</label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Search SKU…"
                    onKeyDown={handleSkuKeyDown}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-centered table-nowrap">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>SKU</th>
                      <th className="text-center">On Hand</th>
                      <th className="text-center">In Transit</th>
                      <th className="text-center">Total</th>
                    </tr>
                  </thead>
                  {rowsState === 'success' ? (
                    <tbody>
                      {filtered.length > 0 ? (
                        filtered.map((r, key) => (
                          <tr key={`${r.region}-${r.sku}-${key}`}>
                            <td>{r.region}</td>
                            <td className="font-weight-bold">{r.sku}</td>
                            <td className="text-center">{r.onHand ?? 0}</td>
                            <td className="text-center">
                              {(r.inTransitOriginToWh ?? 0) > 0 ? (
                                <button
                                  type="button"
                                  className="text-info font-weight-bold"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                  title="Show contributing containers"
                                  onClick={() => setDrill({ sku: r.sku, region: r.region })}
                                >
                                  +{r.inTransitOriginToWh}
                                </button>
                              ) : (
                                0
                              )}
                            </td>
                            <td className="text-center font-weight-bold">
                              {(r.onHand ?? 0) + (r.inTransitOriginToWh ?? 0)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            No stock rows
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ) : (
                    <TableSkeleton columns={5} perPage={10} centerIndex={[2, 3, 4]} />
                  )}
                </table>
              </div>

              {drill ? (
                <div className="mt-4" style={{ background: '#f6f6f6', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>
                      In-transit containers — {drill.sku} ({drill.region})
                    </strong>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      onClick={() => setDrill(null)}
                    >
                      ✕
                    </button>
                  </div>
                  <table className="table table-sm table-nowrap mt-2">
                    <thead>
                      <tr>
                        <th>Container</th>
                        <th>Status</th>
                        <th className="text-center">Qty</th>
                        <th>ETA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(drillRows) ? drillRows : []).map((d, i) => (
                        <tr key={d.container || i}>
                          <td>{d.ref || d.container}</td>
                          <td>{d.status}</td>
                          <td className="text-center">{d.qty}</td>
                          <td>{d.eta ? new Date(d.eta).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                      {Array.isArray(drillRows) && drillRows.length === 0 ? (
                        <tr><td colSpan={4} className="text-center">No containers found</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </CardBody>
          </Card>
        </Col>
      </div>
    </SupplyChainNav>
  );
};

export default TotalStockList;
